/*
 * SimpleCard
 * @module components
 * @flow
 */

import React, { Fragment } from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp, ifNotProp } from 'styled-tools';
import { transparentize } from 'polished';
import * as R from 'ramda';

import { Block } from '~/components/Block';
import { Loader } from '~/components/Loader';
import { DataValue } from '~/components/DataValue';

export type SimpleCardProps = {
  className?: string,
  title?: string,
  units?: string,
  value?: number | string,
  loading?: boolean,
  isPercents?: boolean,
  onClick?: () => void,
  onBodyClick?: () => void,
};

const boxShadowColor = R.pipe(
  palette('shadow', 1),
  transparentize(0.42),
);

const SimpleCardWrapper = styled(Block).attrs(() => ({
  testid: 'simplecard',
  elevation: 0,
}))`
  ${ifProp(
    { device: 'desktop' },
    css`
      min-width: ${ifProp({ size: 'small' }, '164px', '243px')};
      min-height: 116px;
    `,
  )}
  height: fit-content;
  ${ifProp(
    { size: 'small' },
    css`
      text-align: center;
      cursor: pointer;

      &:hover {
        color: ${palette(0)};
        box-shadow: 0 2px 12px 1px ${boxShadowColor};

        .react-simple-card-title {
          background-color: ${palette(0)};
        }
      }
    `,
  )};
  flex-direction: column;
  color: ${palette(1)};
  background: rgba(29, 146, 234, 0.1);
  border: 1px solid currentColor;

  ${/* sc-selector */ Loader} {
    color: currentColor;
  }
`;

const SimpleCardHeader = styled.h4.attrs(() => ({
  className: 'react-simple-card-title',
}))`
  display: flex;
  align-items: center;
  ${ifProp(
    { size: 'small' },
    css`
      justify-content: center;
      height: 35px;
      font-size: 16px;
      color: ${palette('grayscale', 0, true)};
      background: ${palette(1)};
      border-radius: 6px 6px 0 0;
    `,
    css`
      padding: 19px 24px 0;

      ${ifNotProp(
        { device: 'desktop' },
        css`
          padding-top: 14px;
        `,
      )}
    `,
  )};
  font-weight: 600;
`;

const SimpleCardValue = styled.div`
  width: 100%;
  font-weight: bold;

  ${ifProp(
    { size: 'small' },
    css`
      font-size: 18px;
      font-weight: 600;
    `,
    css`
      ${ifProp(
        { device: 'desktop' },
        css`
          font-size: 36px;
        `,
        css`
          font-size: 24px;
          line-height: 0.83;
        `,
      )};
    `,
  )};
`;

const SimpleCardBody = styled.div`
  display: flex;
  align-items: center;

  ${ifProp(
    { size: 'small' },
    css`
      justify-content: center;
      height: 60px;
    `,
    css`
      ${ifProp(
        { device: 'desktop' },
        css`
          margin-top: 10px;
          padding: 0 24px 27px;
        `,
        css`
          margin-top: 7px;
          padding: 0 24px 14px;
        `,
      )};
    `,
  )};
`;

const SimpleCardLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: inherit;

  ${/* sc-selector */ Loader} {
    color: currentColor;
  }
`;

/**
 * SimpleCard React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {string} [props.className] - class name
 * @param {string} [props.title] - title of the card
 * @param {string} [props.units] - units
 * @param {number} [props.value = 0] - value
 * @param {boolean} [props.loading] - loading flag
 * @param {boolean} [props.isPercents] - percents value flag
 * @param {Function} [props.onClick] - click handler
 * @param {Function} [props.onBodyClick] - click body handler
 * @example <caption>Simple usage</caption>
 * <SimpleCard value={11316200} units="mBTC" title="Total Self Reward Earned" />
 * @returns {React.Node<*>} React-component
 */
export const SimpleCard = ({
  className,
  title,
  units,
  value,
  loading,
  isPercents,
  onClick,
  onBodyClick,
  ...rest
}: SimpleCardProps) => (
  <SimpleCardWrapper
    {...rest}
    className={className}
    onClick={onClick}
    testid="simplecard"
  >
    {loading ? (
      <SimpleCardLoader>
        <Loader />
      </SimpleCardLoader>
    ) : (
      <Fragment>
        <SimpleCardHeader {...rest}>{title}</SimpleCardHeader>
        <SimpleCardBody onClick={onBodyClick} {...rest}>
          <SimpleCardValue {...rest}>
            {typeof value !== 'number' ? (
              value
            ) : (
              <DataValue value={value || 0} percents={isPercents} />
            )}
            {units}
          </SimpleCardValue>
        </SimpleCardBody>
      </Fragment>
    )}
  </SimpleCardWrapper>
);

SimpleCard.defaultProps = {
  value: 0,
  size: 'medium',
  palette: 'primary',
  onClick: () => {},
  onBodyClick: () => {},
};
